import type { ApiSuccess, AuthUser, LicenseContext } from '@chefiapro/shared';
import { ApiError, parseApiResponse, safeFetch, checkDownloadResponse } from './http-errors';
export { ApiError } from './http-errors';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';
let accessToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;
const authChannel =
  typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('chefiapro-auth');

if (authChannel) {
  authChannel.onmessage = (event: MessageEvent<{ type: string; token?: string | null }>) => {
    if (event.data.type === 'ACCESS_TOKEN') {
      accessToken = event.data.token ?? null;
      if (!accessToken && typeof window !== 'undefined')
        window.dispatchEvent(new Event('chefiapro-session-ended'));
    }
  };
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (!token && typeof window !== 'undefined')
    window.dispatchEvent(new Event('chefiapro-session-ended'));
  authChannel?.postMessage({ type: 'ACCESS_TOKEN', token });
}

async function requestSessionRefresh(): Promise<string | null> {
  const response = await safeFetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  const payload = await parseApiResponse<{ accessToken: string }>(response, '/auth/refresh');
  setAccessToken(payload.data.accessToken);
  return payload.data.accessToken;
}

async function refreshSession(): Promise<string | null> {
  if (!refreshPromise) {
    const tokenBeforeRefresh = accessToken;
    const performRefresh = async () => {
      // A mensagem da aba que acabou de liberar o lock é entregue de forma assíncrona.
      await new Promise((resolve) => setTimeout(resolve, 20));
      if (accessToken && accessToken !== tokenBeforeRefresh) return accessToken;
      return requestSessionRefresh();
    };
    const coordinatedRefresh: Promise<string | null> =
      typeof navigator !== 'undefined' && navigator.locks
        ? navigator.locks
            .request<Promise<string | null>>(
              'chefiapro-session-refresh',
              { mode: 'exclusive' },
              performRefresh,
            )
            .then((token) => token)
        : performRefresh();
    refreshPromise = coordinatedRefresh
      .catch((error: unknown) => {
        // Falha de rede não comprova revogação. Não encerra sessões válidas em outras abas.
        if (error instanceof ApiError && [401, 403].includes(error.status)) {
          setAccessToken(null);
          return null;
        }
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// A restauração inicial usa o mesmo lock da renovação por401. Abrir três abas
// simultaneamente não pode disputar o mesmo refresh token por rotas diferentes.
export async function restoreSession(): Promise<{
  accessToken: string;
  user: AuthUser;
  license: LicenseContext;
}> {
  const token = await refreshSession();
  if (!token)
    throw new ApiError(401, 'SESSION_UNAVAILABLE', 'Não foi possível restaurar a sessão.');
  const result = await apiRequest<{ user: AuthUser; license: LicenseContext }>('/auth/session');
  return { accessToken: accessToken ?? token, ...result.data };
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<ApiSuccess<T>> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const response = await safeFetch(`${API_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && retry && !path.startsWith('/auth/')) {
    const token = await refreshSession();
    if (token) return apiRequest<T>(path, init, false);
  }

  return parseApiResponse<T>(response, path);
}

export async function downloadFile(
  path: string,
  retry = true,
): Promise<{ blob: Blob; fileName: string }> {
  const headers = new Headers({ Accept: 'application/pdf, application/octet-stream' });
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  const response = await safeFetch(`${API_URL}${path}`, { headers, credentials: 'include' });
  if (response.status === 401 && retry) {
    const token = await refreshSession();
    if (token) return downloadFile(path, false);
  }
  await checkDownloadResponse(response, path);
  const disposition = response.headers.get('content-disposition') ?? '';
  const encodedName = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const basicName = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  return {
    blob: await response.blob(),
    fileName: encodedName ? decodeURIComponent(encodedName) : (basicName ?? 'arquivo'),
  };
}

export const api = {
  get: <T>(path: string) => apiRequest<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) =>
    apiRequest<T>(path, { method: 'POST', body: formData }),
};
